import React from "react";
import {
  ArrowUpDown,
  BarChart3,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  List,
  Search,
} from "lucide-react";
import CustomDatePicker from "../../components/CustomDatePicker";
import CustomSelect from "../../components/CustomSelect";
import ThemeTooltip from "../../components/ThemeTooltip";
import AppInput from "../../components/ui/AppInput";
import AttendanceDownloadMenu from "./AttendanceDownloadMenu";
import {
  ATTENDANCE_VIEW_DESKTOP_DATE_NAV_BUTTON_CLASS,
  ATTENDANCE_VIEW_DESKTOP_DATE_PICKER_WRAPPER_CLASS,
  ATTENDANCE_VIEW_DESKTOP_GROUP_TOGGLE_WRAPPER_CLASS,
  ATTENDANCE_VIEW_DESKTOP_HEADER_CLASS,
  ATTENDANCE_VIEW_DESKTOP_SEARCH_ICON_CLASS,
  ATTENDANCE_VIEW_DESKTOP_SEARCH_WRAPPER_CLASS,
  ATTENDANCE_VIEW_DESKTOP_TAB_TOGGLE_WRAPPER_CLASS,
  ATTENDANCE_VIEW_DESKTOP_VIEW_MODE_WRAPPER_CLASS,
  getAttendanceViewDesktopGroupButtonClass,
  getAttendanceViewDesktopSearchIconFocusClass,
  getAttendanceViewDesktopSearchInputClass,
  getAttendanceViewDesktopTabButtonClass,
  getAttendanceViewDesktopViewModeButtonClass,
} from "./attendanceViewStyles";

export default function AttendanceDesktopHeader({
  activeTab,
  brand,
  changeDate,
  changeMonth,
  changeYear,
  currentDate,
  currentMonth,
  currentYear,
  handleDownloadExcel,
  handleShareStats,
  isDownloadMenuOpen,
  searchQuery,
  setActiveTab,
  setCurrentDate,
  setCurrentMonth,
  setCurrentYear,
  setIsDownloadMenuOpen,
  setSearchQuery,
  setSortKey,
  setSortOrder,
  setTargetGroup,
  setViewMode,
  sortKey,
  sortOrder,
  targetGroup,
  theme,
  viewMode,
}) {
  return (
    <header className={ATTENDANCE_VIEW_DESKTOP_HEADER_CLASS}>
      <div className="flex items-center gap-2 flex-1 min-w-0 w-full xl:w-auto">
        <div className={ATTENDANCE_VIEW_DESKTOP_GROUP_TOGGLE_WRAPPER_CLASS}>
          <button
            onClick={() => setTargetGroup("students")}
            className={getAttendanceViewDesktopGroupButtonClass(targetGroup === "students", theme.bg)}
          >
            학생
          </button>
          <button
            onClick={() => setTargetGroup("teachers")}
            className={getAttendanceViewDesktopGroupButtonClass(targetGroup === "teachers", theme.bg)}
          >
            교사
          </button>
        </div>

        <div className={ATTENDANCE_VIEW_DESKTOP_TAB_TOGGLE_WRAPPER_CLASS}>
          {["daily", "monthly", "yearly"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={getAttendanceViewDesktopTabButtonClass(activeTab === tab, theme.bg)}
            >
              {tab === "daily" && <><List size={14} /> 일별</>}
              {tab === "monthly" && <><CalendarIcon size={14} /> 월간</>}
              {tab === "yearly" && <><BarChart3 size={14} /> 연간</>}
            </button>
          ))}
        </div>

        {(activeTab === "daily" || activeTab === "monthly" || activeTab === "yearly") && (
          <div className={ATTENDANCE_VIEW_DESKTOP_DATE_PICKER_WRAPPER_CLASS}>
            <button
              onClick={() => (activeTab === "monthly" ? changeMonth(-1) : activeTab === "yearly" ? changeYear(-1) : changeDate(-7))}
              className={ATTENDANCE_VIEW_DESKTOP_DATE_NAV_BUTTON_CLASS}
            >
              <ChevronLeft size={18} />
            </button>
            <div className="px-2 relative z-50">
              <CustomDatePicker
                theme={theme}
                type={activeTab === "monthly" ? "month" : activeTab === "yearly" ? "year" : "date"}
                value={activeTab === "monthly" ? currentMonth : activeTab === "yearly" ? currentYear : currentDate}
                onChange={activeTab === "monthly" ? setCurrentMonth : activeTab === "yearly" ? setCurrentYear : setCurrentDate}
                customTrigger={
                  <span className="text-sm font-black text-gray-700 min-w-[100px] text-center cursor-pointer hover:text-[#2C3E30] transition whitespace-nowrap">
                    {activeTab === "monthly" ? `${currentMonth.split("-")[1]}월` : activeTab === "yearly" ? `${currentYear}년` : currentDate}
                  </span>
                }
              />
            </div>
            <button
              onClick={() => (activeTab === "monthly" ? changeMonth(1) : activeTab === "yearly" ? changeYear(1) : changeDate(7))}
              className={ATTENDANCE_VIEW_DESKTOP_DATE_NAV_BUTTON_CLASS}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 justify-end w-full xl:w-auto flex-wrap xl:flex-nowrap">
        <div className={ATTENDANCE_VIEW_DESKTOP_SEARCH_WRAPPER_CLASS}>
          <AppInput
            id="attendance-desktop-search"
            name="attendance_desktop_search"
            placeholder="학생 검색..."
            className={getAttendanceViewDesktopSearchInputClass(theme)}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery("")}
            icon={Search}
          />
        </div>

        {activeTab === "daily" && (
          <>
            <div className="flex items-center gap-2 flex-shrink-0">
              <CustomSelect
                theme={theme}
                className="w-[100px] flex-shrink-0"
                value={sortKey}
                onChange={setSortKey}
                options={
                  targetGroup === "students"
                    ? [
                        { value: "grade", label: "학년순" },
                        { value: "class", label: "반별순" },
                        { value: "name", label: "이름순" },
                        { value: "status", label: "상태순" },
                        { value: "department", label: "부서순" },
                      ]
                    : [
                        { value: "responsibility", label: "담당순" },
                        { value: "role", label: "직분순" },
                        { value: "name", label: "이름순" },
                        { value: "status", label: "상태순" },
                        { value: "department", label: "부서순" },
                      ]
                }
                showSearch={false}
                placeholder="정렬"
              />
              <button
                onClick={() => setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))}
                className={`w-11 h-11 flex items-center justify-center bg-white rounded-full border border-gray-200 shadow-sm transition-all hover:bg-gray-50 active:scale-95 ${sortOrder === "desc" ? theme.text : "text-gray-400"}`}
                title={sortOrder === "asc" ? "오름차순" : "내림차순"}
              >
                <ArrowUpDown size={18} className={sortOrder === "desc" ? "rotate-180" : ""} />
              </button>
            </div>

            <div className={ATTENDANCE_VIEW_DESKTOP_VIEW_MODE_WRAPPER_CLASS}>
              <ThemeTooltip theme={theme} text="카드형 보기">
                <button onClick={() => setViewMode("card")} className={getAttendanceViewDesktopViewModeButtonClass(viewMode === "card", theme)}>
                  <LayoutGrid size={18} />
                </button>
              </ThemeTooltip>
              <ThemeTooltip theme={theme} text="리스트형 보기">
                <button onClick={() => setViewMode("list")} className={getAttendanceViewDesktopViewModeButtonClass(viewMode === "list", theme)}>
                  <List size={18} />
                </button>
              </ThemeTooltip>
            </div>

            <AttendanceDownloadMenu
              handleDownloadExcel={handleDownloadExcel}
              handleShareStats={handleShareStats}
              isOpen={isDownloadMenuOpen}
              setIsOpen={setIsDownloadMenuOpen}
              triggerClassName={`w-11 h-11 flex items-center justify-center bg-white ${brand.primaryTextClass} rounded-full hover:bg-gray-50 transition shadow-sm border border-gray-200 active:scale-95 flex-shrink-0`}
            />
          </>
        )}

        {(activeTab === "monthly" || activeTab === "yearly") && (
          <div className="flex gap-2">
            <AttendanceDownloadMenu
              handleDownloadExcel={handleDownloadExcel}
              handleShareStats={handleShareStats}
              isOpen={isDownloadMenuOpen}
              setIsOpen={setIsDownloadMenuOpen}
              triggerClassName={`w-11 h-11 flex items-center justify-center bg-white ${theme.text} rounded-full hover:bg-gray-50 transition shadow-sm border border-gray-200 active:scale-95 flex-shrink-0`}
            />
          </div>
        )}
      </div>
    </header>
  );
}
